/* Pricing Recommendation Card — P2 advisory layer.
 *
 * Displays the engine output as a CFO-grade explainer panel:
 *   • Negotiation Buffer + suggested total with buffer
 *   • Revision Forecast + expected approval days + close probability
 *   • Approval Friction score
 *   • Recommended Advance Payment %
 *   • Recommended Discount Ceiling
 *   • Recommended Payment Terms
 *   • Reasons list (so the CFO can read WHY)
 *
 * Strict UX rule (user mandate): Advisory only.
 * No "Apply" button — the engine never mutates a quotation.
 *
 * Props
 *   quotationId  — required; the engine returns customer-aware advice
 *                   contextualised against this draft's offer price.
 */
import React, { useEffect, useState } from 'react';
import { axiosInstance } from '../App';
import { TrendingUp, AlertTriangle, Clock, Target, Wallet, FileText, Info } from 'lucide-react';


const PricingRecommendationCard = ({ quotationId }) => {
  const [rec, setRec] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!quotationId) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const r = await axiosInstance.get(
          `/quotations/${quotationId}/pricing-recommendation`,
        );
        if (!cancelled) setRec(r.data || null);
      } catch (_) {
        if (!cancelled) setRec(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [quotationId]);

  if (loading || !rec) return null;
  if (rec.sample_size === 0) {
    // No history yet — render a quiet "learning" chip rather than nothing.
    return (
      <div data-testid="pricing-rec-empty" className="mb-3 px-3 py-2 rounded-lg border border-gray-200 bg-gray-50 text-xs text-gray-600 flex items-center gap-2">
        <Info className="w-3.5 h-3.5" />
        Pricing engine is learning — no frozen contracts yet for this customer.
      </div>
    );
  }

  const buffer       = rec.negotiation_buffer_pct;
  const suggested    = rec.suggested_total_with_buffer;
  const bufferAmount = rec.buffer_amount;
  const ceiling      = rec.recommended_discount_ceiling_pct;
  const advance      = rec.recommended_advance_payment_pct;
  const closeProb    = rec.expected_close_probability;
  const friction     = rec.approval_friction_score;

  return (
    <div
      data-testid="pricing-recommendation-card"
      className="mb-3 border border-amber-300 bg-amber-50 rounded-lg overflow-hidden"
    >
      <div className="px-3 py-2 bg-amber-100 border-b border-amber-300 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-amber-900 font-semibold text-sm">
          <Target className="w-4 h-4" />
          Pricing Recommendation
          <span className="text-xs font-normal opacity-70" data-testid="pricing-rec-advisory-tag">
            · advisory only
          </span>
        </div>
        <button
          data-testid="pricing-rec-expand-toggle"
          onClick={() => setExpanded((v) => !v)}
          className="text-xs px-2 py-0.5 rounded border border-amber-700/30 text-amber-900 hover:bg-white/40"
        >
          {expanded ? 'Hide reasons' : 'Why?'}
        </button>
      </div>

      <div className="px-3 py-3 space-y-2 text-sm text-amber-950">
        {/* Buffer row */}
        {buffer != null && buffer > 0 && (
          <div className="flex items-center justify-between gap-2 bg-white/60 rounded px-2 py-1.5 border border-amber-200">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-3.5 h-3.5" />
              Negotiation buffer
              <span className="font-bold" data-testid="pricing-rec-buffer-pct">
                +{buffer.toFixed(1)}%
              </span>
            </div>
            {suggested != null && (
              <span data-testid="pricing-rec-suggested-total"
                    className="text-xs opacity-80 font-mono">
                Suggested total: <strong>{suggested.toLocaleString()}</strong> SAR
                {bufferAmount != null && (
                  <span className="opacity-70"> (+{bufferAmount.toLocaleString()})</span>
                )}
              </span>
            )}
          </div>
        )}

        <div className="grid grid-cols-2 gap-2 text-xs">
          {/* Discount ceiling */}
          {ceiling != null && (
            <div className="bg-white/60 rounded px-2 py-1.5 border border-amber-200" data-testid="pricing-rec-ceiling">
              <div className="opacity-70">Max safe discount</div>
              <div className="font-semibold flex items-center gap-1">
                <AlertTriangle className="w-3 h-3 text-rose-600" />
                {ceiling.toFixed(1)}%
              </div>
            </div>
          )}
          {/* Advance payment */}
          {advance != null && (
            <div className="bg-white/60 rounded px-2 py-1.5 border border-amber-200" data-testid="pricing-rec-advance">
              <div className="opacity-70">Recommended advance</div>
              <div className="font-semibold flex items-center gap-1">
                <Wallet className="w-3 h-3 text-emerald-700" />
                {advance}%
              </div>
            </div>
          )}
          {/* Expected revisions */}
          {rec.expected_revisions != null && (
            <div className="bg-white/60 rounded px-2 py-1.5 border border-amber-200" data-testid="pricing-rec-revisions">
              <div className="opacity-70">Expected revisions</div>
              <div className="font-semibold">{rec.expected_revisions}</div>
            </div>
          )}
          {/* Close probability */}
          {closeProb != null && (
            <div className="bg-white/60 rounded px-2 py-1.5 border border-amber-200" data-testid="pricing-rec-close-prob">
              <div className="opacity-70">Historic close rate</div>
              <div className="font-semibold">{(closeProb * 100).toFixed(0)}%</div>
            </div>
          )}
          {/* Approval days */}
          {rec.expected_approval_days != null && (
            <div className="bg-white/60 rounded px-2 py-1.5 border border-amber-200" data-testid="pricing-rec-days">
              <div className="opacity-70">Avg approval days</div>
              <div className="font-semibold flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {rec.expected_approval_days}
              </div>
            </div>
          )}
          {/* Approval friction */}
          {friction != null && friction > 0 && (
            <div className="bg-white/60 rounded px-2 py-1.5 border border-amber-200" data-testid="pricing-rec-friction">
              <div className="opacity-70">Approval friction</div>
              <div className={`font-semibold ${friction >= 50 ? 'text-rose-700' : ''}`}>
                {friction.toFixed(0)}/100
              </div>
            </div>
          )}
        </div>

        {/* Payment terms suggestion */}
        {rec.recommended_payment_terms && (
          <div className="bg-white/60 rounded px-2 py-1.5 border border-amber-200 text-xs flex items-start gap-2" data-testid="pricing-rec-terms">
            <FileText className="w-3.5 h-3.5 mt-0.5 opacity-70" />
            <div>
              <div className="opacity-70 mb-0.5">Recommended payment terms</div>
              <div className="font-medium">{rec.recommended_payment_terms}</div>
            </div>
          </div>
        )}

        {/* Sample size + advisory note */}
        <div className="text-[11px] opacity-70 italic flex items-center gap-2" data-testid="pricing-rec-sample-size">
          <span>Based on {rec.sample_size} frozen contract{rec.sample_size === 1 ? '' : 's'} for this customer.</span>
          {rec.confidence_score != null && rec.confidence_score > 0 && (
            <span
              data-testid="pricing-rec-confidence-chip"
              title={`Confidence is a function of sample size — 1=30%, 5=70%, 10+=90%`}
              className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded font-mono text-[10px] not-italic ${
                rec.confidence_score >= 70
                  ? 'bg-emerald-100 text-emerald-800 border border-emerald-200'
                  : rec.confidence_score >= 45
                    ? 'bg-amber-100 text-amber-800 border border-amber-200'
                    : 'bg-gray-100 text-gray-700 border border-gray-200'
              }`}
            >
              confidence {rec.confidence_score}%
            </span>
          )}
        </div>

        {expanded && (rec.reasons || []).length > 0 && (
          <ul className="mt-2 space-y-1 text-xs border-t border-amber-200 pt-2" data-testid="pricing-rec-reasons">
            {rec.reasons.map((reason, i) => (
              <li key={i} className="flex items-start gap-1" data-testid={`pricing-rec-reason-${i}`}>
                <span className="opacity-70">·</span> {reason}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default PricingRecommendationCard;
